//-tech-debt-react-query;
import { useEffect, useMemo, useRef } from "react";
import { useQuery, useQueryClient, useMutation, type UseQueryOptions } from "@tanstack/react-query";
import { CacheKey } from "../types/cache-keys";
import {
  ApprovalRequest,
  ApprovalRequestStatusLevel,
  ApprovalRequestType,
  EscalationApproval,
  EscalationRequest,
  EscalationRequestPayload,
  EscalationType
} from "../types/approval-request";
import { fetchItem, fetchItems, postItem, putItem } from "../support/queries";
import {
  getJiraExternalTicket,
  isJiraTicketResolved,
} from "../components/Opportunity/Escalation/opportunityEscalationHelpers";
import { Opportunity } from "../types/opportunity";
import { Product } from "../types/product";
import { User } from "../types/user";
import { salesforceId, useFetchItem, useFetchItems, validId } from "./base-hooks";


interface EscalationWorkflowPayload {
  create_escalation: {
    opportunity_id: Opportunity['id'];
    product_id: Product['id'];
    escalation_type_id: number;
    requested_by_id: User['id'];
    status_id: ApprovalRequestStatusLevel;
    description: string;
  };
  create_approval_request: {
    title: string;
    requested_item_type: ApprovalRequestType;
    justification: string;
    requestor_id: User['id'];
    approver_id: User['id'];
    status_id: ApprovalRequestStatusLevel;
  };
  create_escalation_approval: {
    level: number;
    target_assignment_type_id: number;
  };
}

interface EscalationWorkflowResponse {
  escalation: EscalationRequest;
  approval_request: ApprovalRequest;
  escalation_approval: EscalationApproval;
  created: {
    escalation: boolean;
    approval_request: boolean;
    escalation_approval: boolean;
    assignment: boolean;
  };
}

const useFetchEscalationRequests = (opportunityId: string, params = {}, options = {}) => {
  const queryClient = useQueryClient();
  const { onSuccess, ...queryOptions } = options as UseQueryOptions<EscalationRequest[], Error> & {
    onSuccess?: (escalations: EscalationRequest[]) => void;
  };
  const optionsRef = useRef({ params, onSuccess });
  optionsRef.current = { params, onSuccess };

  const query = useQuery<EscalationRequest[]>({
    queryKey: [CacheKey.EscalationRequest, opportunityId],
    queryFn: () => fetchItems(`db/escalation`, { ...params, opportunity: opportunityId, detailed: true, depth: 2 }, options),
    ...queryOptions,
  });

  useEffect(() => {
    const escalations = query.data;
    if (!escalations) return;

    escalations.forEach((escalation) => {
      if (!validId(escalation?.id)) return;

      const childKey = [CacheKey.EscalationRequest, opportunityId, String(escalation.id)];
      const currentParams = optionsRef.current.params;

      queryClient.setQueryDefaults(childKey, {
        queryFn: () => fetchItem(`db/escalation/${escalation.id}`, { ...currentParams, detailed: true }),
      });
      queryClient.setQueryData(childKey, escalation);
    });

    optionsRef.current.onSuccess?.(escalations);
  }, [query.dataUpdatedAt, query.data, queryClient, opportunityId]);

  return query;
};

const useFetchEscalationRequest = (
  opportunityId: Opportunity['id'],
  escalationId: EscalationRequest['id'],
  params = {},
  options?
) => useFetchItem<EscalationRequest>(
  'db/escalation',
  escalationId,
  () => [CacheKey.EscalationRequest, opportunityId],
  { ...params, detailed: true },
  { ...options,
    enabled: typeof options?.enabled === "boolean" 
      ? options.enabled && salesforceId(opportunityId) && validId(escalationId)
      : salesforceId(opportunityId) && validId(escalationId)
  }
);

const useRefetchEscalationRequests = (opportunityId: string, params = {}, options = {}) => {
  const queryClient = useQueryClient();

  return async (): Promise<EscalationRequest[]> => {
    const escalations = await fetchItems(
      'db/escalation',
      { ...params, opportunity: opportunityId, detailed: true, depth: 2 },
      options
    );

    const normalizedEscalations = Array.isArray(escalations) ? escalations : [];
    queryClient.setQueryData([CacheKey.EscalationRequest, opportunityId], normalizedEscalations);

    return normalizedEscalations;
  };
};

const useCreateEscalationWorkflowMutation = (opportunityId: string) => {
  const queryClient = useQueryClient();
  const refetchEscalationRequests = useRefetchEscalationRequests(opportunityId);

  return useMutation<EscalationWorkflowResponse, Error, EscalationWorkflowPayload>({
    mutationFn: (payload) => postItem('db/escalation-workflow', payload),
    onSuccess: async (response: EscalationWorkflowResponse) => {
      const escalationId = response?.escalation?.id;

      queryClient.invalidateQueries({
        queryKey: [CacheKey.ApprovalRequest, 'list'],
        refetchType: 'all',
      });

      if (salesforceId(opportunityId) && validId(escalationId)) {
        const childKey = [CacheKey.EscalationRequest, String(opportunityId), String(escalationId)];
        queryClient.setQueryDefaults(childKey, {
          queryFn: () => fetchItem(`db/escalation/${escalationId}`, { detailed: true }),
        });

        const freshList = await refetchEscalationRequests();
        const freshEscalation = freshList.find((e) => e.id === escalationId);
        if (freshEscalation) {
          queryClient.setQueryData(childKey, freshEscalation);
        }
      }
    },
    onError: (error: Error) => {
      console.error('Escalation workflow creation failed:', error);
    },
  });
};

// Note: not using base caching here - that code would trigger a full fetch of the escalations list including resetting all child escalation caches
const useUpdateEscalationMutation = (opportunityId: string) => {
  const queryClient = useQueryClient();
  const refetchEscalationRequests = useRefetchEscalationRequests(opportunityId);

  return useMutation<
    EscalationRequestPayload,
    Error,
    Partial<EscalationRequest> & { id: number }
  >({
    mutationFn: (payload) => putItem('db/escalation', payload),
    onSuccess: async (_response, payload) => {
      const childKey = [CacheKey.EscalationRequest, String(opportunityId), String(payload.id)];
      queryClient.setQueryDefaults(childKey, {
        queryFn: () => fetchItem(`db/escalation/${payload.id}`, { detailed: true }),
      });

      const freshList = await refetchEscalationRequests();
      const freshEscalation = freshList.find((e) => e.id === payload.id);
      if (freshEscalation) {
        queryClient.setQueryData(childKey, freshEscalation);
      }
    },
  });
}

/** How often an open BU escalation re-reads its Jira ticket while the page is being viewed. */
const JIRA_SYNC_POLL_INTERVAL_MS = 60 * 1000;

/**
 * Keep a BU escalation's Jira ticket state current.
 *
 * The escalation payload only carries what the last sync stored, so an issue that gets picked
 * up or transitioned in Jira would otherwise keep showing its state at creation time. This asks
 * the backend to refresh from Jira (`sync_jira=true`, which the normal lookups deliberately
 * skip because the Jira round trip is slow) and merges just the Jira fields into the escalation
 * already on screen, so a slow sync never blanks out the rest of the row.
 *
 * Polls while mounted and stops once the issue is resolved.
 */
const useSyncEscalationJiraTicket = (
  opportunityId: Opportunity['id'],
  escalationId: EscalationRequest['id'],
  options: { enabled?: boolean } = {}
) => {
  const queryClient = useQueryClient();
  const enabled = (options.enabled ?? true) && salesforceId(opportunityId) && validId(escalationId);

  const escalationKey = useMemo(
    () => [
      CacheKey.EscalationRequest,
      String(opportunityId),
      String(escalationId),
    ],
    [opportunityId, escalationId],
  );

  const query = useQuery<EscalationRequest>({
    queryKey: [...escalationKey, 'jira-sync'],
    queryFn: () => fetchItem(`db/escalation/${escalationId}`, {
      detailed: true,
      sync_jira: true,
      include_external_tickets: true,
    }),
    enabled,
    staleTime: JIRA_SYNC_POLL_INTERVAL_MS,
    refetchInterval: (query) => {
      const data = query.state.data;
      return data && isJiraTicketResolved(getJiraExternalTicket(data))
        ? false
        : JIRA_SYNC_POLL_INTERVAL_MS;
    },
    refetchOnWindowFocus: true,
  });

  useEffect(() => {
    const synced = query.data;
    if (!synced) return;

    queryClient.setQueryData<EscalationRequest>(escalationKey, (previous) => {
      if (!previous) return synced;

      return {
        ...previous,
        jira_issue_id: synced.jira_issue_id ?? previous.jira_issue_id,
        external_tickets: synced.external_tickets ?? previous.external_tickets,
      };
    });
  }, [query.dataUpdatedAt, query.data, queryClient, escalationKey]);

  useEffect(() => {
    if (!query.error) return;
    console.warn(`Jira sync failed for escalation ${escalationId}:`, query.error);
  }, [query.error, escalationId]);

  return query;
};

const useFetchEscalationTypes = (options = {}) =>
  useFetchItems<EscalationType[]>(
    'db/escalation-type',
    CacheKey.EscalationType,
    {},
    options
  );

export {
  useFetchEscalationRequests,
  useFetchEscalationRequest,
  useRefetchEscalationRequests,
  useCreateEscalationWorkflowMutation,
  useUpdateEscalationMutation,
  useFetchEscalationTypes,
  useSyncEscalationJiraTicket,
}